import type { StampMilestone } from '../../../services/shop-service';

/**
 * Validates and normalises a shop's milestone ladder.
 *
 * Lives outside RewardsSettings.tsx because that file is already ~750 lines,
 * and because the rules are worth testing without mounting a form.
 *
 * Always returns the SORTED list even on failure, so a rejecting form can
 * still render rows in the order the owner will see them after a fix.
 */
export function validateMilestones(
  list: StampMilestone[],
): { value: StampMilestone[]; error: string | null } {
  const sorted = [...list].sort((a, b) => a.at - b.at);

  for (const m of sorted) {
    if (!Number.isInteger(m.at) || m.at < 1) {
      return { value: sorted, error: 'A milestone needs at least 1 stamp.' };
    }
    if (!m.name.trim()) {
      return { value: sorted, error: 'Every milestone needs a name — staff read it at the counter.' };
    }
  }

  const seen = new Set<number>();
  for (const m of sorted) {
    if (seen.has(m.at)) {
      return { value: sorted, error: 'Two milestones share the same number of stamps.' };
    }
    seen.add(m.at);
  }

  return { value: sorted, error: null };
}

/**
 * The highest milestone a customer has crossed but not yet claimed.
 *
 * Shared by the settings preview and the API handler so the counter and the
 * customer's pass can never disagree about what is owed.
 */
export function highestUnclaimed(
  milestones: StampMilestone[],
  lifetimeStamps: number,
  claimedAt: number[],
): StampMilestone | null {
  const claimed = new Set(claimedAt);
  const crossed = milestones
    .filter((m) => m.at <= lifetimeStamps && !claimed.has(m.at))
    .sort((a, b) => b.at - a.at);
  return crossed[0] ?? null;
}
